import type { Patient, Practitioner, Reference } from '@medplum/fhirtypes'
import { displayFromCodeableConcept } from '@seen/core/codeableConcept'
import { referenceDisplay } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { Assessment } from '@seen/core/models/assessments/assessment'
import { IDT_ROLE_OPTIONS_WITH_KEYWORDS } from '@seen/core/models/care-teams/constants'
import { NonVisitEncounter } from '@seen/core/models/encounters/nonVisitEncounter'
import { NoteLabel } from '@seen/core/models/noteLabel'
import { NoteTemplate } from '@seen/core/models/noteTemplate'
import { ParticipantNote } from '@seen/core/models/participantNote'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { TemplateProvenance } from '@seen/core/models/templateProvenance'
import { VisitNote } from '@seen/core/models/visitNote'
import {
  ASSESSMENT_VISIT_NOTE_CATEGORIES,
  isAssessmentVisitNoteCategoryToken,
  noteLabelCategoryFilter,
  VISIT_NOTE_CATEGORIES,
  visitNoteCategoryToken,
} from '@seen/core/models/visitNoteCategory'
import { resolveId } from '@seen/core/references'
import type { Role } from '@seen/core/roles'
import { isOfType, now } from '@seen/core/utils'
import {
  Badge,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TypographyPageTitle,
} from '@seen/ui'
import {
  IconCalendarEvent,
  IconPlus,
  IconUser,
  IconUsersGroup,
} from '@tabler/icons-react'
import { keepPreviousData } from '@tanstack/react-query'
import type {
  ColumnDef,
  OnChangeFn,
  SortingFn,
  SortingState,
} from '@tanstack/react-table'
import { uniq, uniqBy } from 'lodash-es'
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router'
import { ContextMenuOpenInNewTab } from '#src/components/ContextMenuOpenInNewTab.tsx'
import { ContextMenuOpenInNewWindow } from '#src/components/ContextMenuOpenInNewWindow.tsx'
import {
  getSortableColumns,
  SearchSortChip,
} from '#src/components/core/data-table/SearchSortChip.tsx'
import { HighlightedText } from '#src/components/core/HighlightedText.tsx'
import { HoverPractitionerAvatar } from '#src/components/core/hover-card/index.ts'
import { ShortcutSearchInput } from '#src/components/core/input/ShortcutSearchInput.tsx'
import { SearchMatchSnippet } from '#src/components/core/SearchMatchSnippet.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterComposer } from '#src/components/filter/FilterComposer.tsx'
import {
  FilterValue,
  useFilteredData,
} from '#src/components/filter/useFilteredData.ts'
import { IconLabel } from '#src/components/icons/IconLabel.tsx'
import { DataTable, HoverDate, Loader } from '#src/components/index.ts'
import { LabelColor } from '#src/components/LabelColor.tsx'
import { LabelOption } from '#src/components/LabelOption.tsx'
import { TemplateCombobox } from '#src/components/TemplateCombobox.tsx'
import { useLabelFilterOptions } from '#src/components/UseLabelFilterOptions.tsx'
import {
  useCurrentPractitioner,
  useNavigateFromClickEvent,
  usePatchMutation,
  usePractitionersInternal,
  useSearch,
  useTransactionMutation,
} from '#src/hooks/index.ts'
import {
  countStemOccurrencesMulti,
  type MatchInfo,
  useClientSideSearch,
} from '#src/hooks/useClientSideSearch.ts'
import { useDebounced } from '#src/hooks/useDebounced.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import {
  type ParticipantNotesView,
  useNotesViewStore,
} from '#src/hooks/useNotesViewStore.ts'
import { getParticipantNoteTitle } from '#src/hooks/useParticipantNoteTitle.ts'
import { useReference } from '#src/hooks/useReference.ts'
import { ownerFilterLabel } from '#src/pages/participants/visit-notes/ownerFilterLabel.ts'
import {
  extractTextFromHTML,
  extractTitleFromHTML,
  getNoteName,
  getNoteSignedOn,
  openInNewWindow,
  toast,
} from '#src/utils/index.ts'

/* The Notes table is a list-layer consolidation of two surfaces: notes
 * (Elation VisitNotes + ParticipantNotes, `category=note`) and the legacy
 * assessments (`category=assessment`). Rows are real model instances, so we
 * classify them with `instanceof` (matching the existing note branching) —
 * the model `isInstance` predicates operate on raw FHIR resources, not on the
 * wrapper instances `useSearch` returns. The editors stay separate; only the
 * list is unified. */
type NoteRow = VisitNote | ParticipantNote | Assessment

const COLUMN_WIDTHS = {
  date: 96,
  signed: 96,
  /* Just wide enough for the 20px owner avatar plus cell padding; it sits
   * far-right as a trailing column (matching the Tasks list). */
  owner: 48,
} as const

/* Assessments open the legacy assessment editor; notes open the collab note
 * editor. */
function getRowHref(row: NoteRow, patientId: string): string {
  return row instanceof Assessment
    ? `/Patient/${patientId}/assessments/${row.id}`
    : `/Patient/${patientId}/visit-notes/${row.id}`
}

/* URL keys that round-trip the active sort. With `searchKeepsTableSort` off,
 * a missing `sortBy` while searching means relevance. With it on, missing
 * means the table default and `sortBy=relevance` is the explicit relevance
 * order. */
const SORT_BY_PARAM = 'sortBy'
const SORT_DIR_PARAM = 'sortDir'
const RELEVANCE_SORT_BY = 'relevance'

/* Where the table lands with no `?sortBy=` in the URL, and — when
 * `searchKeepsTableSort` is on — where a search lands too. */
const DEFAULT_SORTING: SortingState = [{ id: 'date', desc: true }]

function getNoteOwner(note: NoteRow): string {
  const signer = getRowSigner(note)
  if (signer) {
    return referenceDisplay(signer) ?? ''
  }
  const owner = getRowAuthor(note)
  return isOfType(owner, 'Practitioner') ? (referenceDisplay(owner) ?? '') : ''
}

/* TanStack's `auto` resolver can land on its case-sensitive basic comparator,
 * which would interleave `alice` and `Alice` unpredictably. Base sensitivity
 * folds case and accents together, and equal values keep their incoming
 * (relevance) order because TanStack's sort is stable. */
const sortNoteText: SortingFn<NoteRow> = (rowA, rowB, columnId) =>
  String(rowA.getValue(columnId)).localeCompare(
    String(rowB.getValue(columnId)),
    undefined,
    { sensitivity: 'base' }
  )

/* Owner-precedence helpers shared by the owner column, owner filter, and
 * sort. VisitNote signs via `signedBy`; ParticipantNote via `signerReference`;
 * Assessment via `signer`. The authoring practitioner is `author[0]` for
 * VisitNote, `owner` for ParticipantNote, and `author` for Assessment. */
function getRowSigner(note: NoteRow) {
  if (note instanceof VisitNote) {
    return note.signedBy
  }
  if (note instanceof Assessment) {
    return note.signer
  }
  return note.signerReference
}

function getRowAuthor(note: NoteRow) {
  if (note instanceof VisitNote) {
    return note.author[0]
  }
  if (note instanceof Assessment) {
    return note.author
  }
  return note.owner
}

/* Resolves the owner reference object (not just its string) so the Owner
 * filter and its options share one source of truth. ParticipantNote keys on
 * its author (`owner`); VisitNote/Assessment prefer the signer, then author. */
function getNoteOwnerReferenceObject(
  note: NoteRow
): Reference<Practitioner> | undefined {
  if (note instanceof VisitNote) {
    const owner = note.signedBy ?? note.author[0]
    return isOfType(owner, 'Practitioner') ? owner : undefined
  }
  if (note instanceof Assessment) {
    return note.signer ?? note.author
  }
  return note.owner
}

function getNoteOwnerReference(note: NoteRow): string {
  return getNoteOwnerReferenceObject(note)?.reference ?? ''
}

function getNoteCreated(note: NoteRow): string {
  if (note instanceof VisitNote) {
    return note.date ?? ''
  }
  if (note instanceof Assessment) {
    return note.startsAt ?? ''
  }
  /* Sorting only — the cell beside this renders `note.period`, the calendar day.
   * A note edited later the same day sorts by that fuller timestamp so same-day
   * notes order by time; what is displayed stays the day. */
  const day = note.displayDate
  const compositionDate = note.dateString
  if (day && compositionDate?.startsWith(day)) {
    return compositionDate
  }
  return day ?? ''
}

// Sorting wants a string; the cell beside it renders the same value.
function getNoteSigned(note: NoteRow): string {
  return getNoteSignedOn(note) ?? ''
}

/* Label + category tokens for the Labels filter facet. ParticipantNotes and
 * Assessments both carry inline labels (meta.tag) and categories
 * (Composition.category[]) under the same vocabulary; Elation VisitNotes do not,
 * so they contribute no tokens and are excluded whenever an inclusion label
 * filter is active. */
function getNoteLabelTokens(note: NoteRow): string[] {
  return note instanceof ParticipantNote || note instanceof Assessment
    ? [...note.labels, ...note.categories]
    : []
}

/* The assessment-kind category tokens that define the Assessments tab, sourced
 * from the core registry so the tab tracks the vocabulary automatically. */
const ASSESSMENT_CATEGORY_TOKENS = ASSESSMENT_VISIT_NOTE_CATEGORIES.map(
  visitNoteCategoryToken
)

/* Assessments-tab membership: a note or assessment carrying at least one
 * assessment-kind visit-note category on `Composition.category[]`. Elation
 * VisitNotes carry no categories, so they never qualify. */
function hasAssessmentCategory(note: NoteRow): boolean {
  return (
    (note instanceof ParticipantNote || note instanceof Assessment) &&
    note.categories.some(isAssessmentVisitNoteCategoryToken)
  )
}

/* AND-combine `_filter` fragments (each an already-parenthesized OR-group from
 * `noteLabelCategoryFilter`), dropping empties. Lets the Assessments view scope to
 * the assessment-kind categories AND an active Labels selection in one server
 * query, so matches past the first page stay in range. */
function combineFilters(
  ...queries: ReturnType<typeof noteLabelCategoryFilter>[]
): ReturnType<typeof noteLabelCategoryFilter> {
  const clauses = queries
    .map((query) => query._filter)
    .filter((clause): clause is string => typeof clause === 'string')
  return clauses.length > 0 ? { _filter: clauses.join(' and ') } : {}
}

interface ExtractNoteSearchFieldsResult {
  [key: string]: string
}

/* Reason excluded — Elation notes fold reason into the title already.
 * Author refs are deduped before joining: signed Elation notes typically
 * carry the same practitioner in both `signedBy` and `author[0]`, which
 * would otherwise double the BM25 term frequency for their name. */
function extractNoteSearchFields(note: NoteRow): ExtractNoteSearchFieldsResult {
  /* Assessments have no AI summary or pre-indexed `searchText`. Index the
   * analogous fields: title, section body text, author display, and the type
   * display label (so "180 day" finds a 180 Days assessment). */
  if (note instanceof Assessment) {
    const authorDisplays = [
      ...new Set(
        [note.signer, note.author].flatMap((r) =>
          referenceDisplay(r) ? [referenceDisplay(r)] : []
        )
      ),
    ]
    const sectionText = note.sections
      .map((section) => extractTextFromHTML(section.text?.div ?? ''))
      .join(' ')
    const typeLabel = displayFromCodeableConcept(note.type) ?? ''
    return {
      title: extractTextFromHTML(note.title),
      summary: '',
      body: [sectionText, typeLabel].filter(Boolean).join(' '),
      author: authorDisplays.join(' '),
    }
  }

  const authorRefs =
    note instanceof VisitNote
      ? [note.signedBy, ...(note.author ?? [])]
      : [note.signerReference, note.owner]
  const authorDisplays = [
    ...new Set(
      authorRefs.map((r) => referenceDisplay(r)).filter((d): d is string => !!d)
    ),
  ]
  return {
    title:
      note instanceof VisitNote
        ? extractTextFromHTML(note.title ?? '')
        : getParticipantNoteTitle(note),
    summary: note.aiSummary ?? '',
    body: note.searchText ?? '',
    author: authorDisplays.join(' '),
  }
}

export function ParticipantVisitNotesPage(): JSX.Element {
  // All / My / Assessments are one `notes/:view` route (not three), so toggling
  // between them re-renders this same mounted component instead of remounting
  // the page — which (with keepPreviousData below) is what stops the first-switch
  // white flash. `:view` is a free segment, so anything but `mine`/`assessments`
  // is the All view.
  const { patientId, view } = useParams()
  invariant(patientId, 'Patient ID is required')
  const notesView: ParticipantNotesView =
    view === 'mine' ? 'mine' : view === 'assessments' ? 'assessments' : 'all'
  // Remember the view the user is actually on so the chart Notes nav can reopen
  // it for every participant. Tied to mount + `view` changes (route-active), not
  // to clicking the picker — so deep-linking or refreshing on /notes/mine also
  // records "mine". Switching browser tabs doesn't remount, so it never clobbers
  // the stored preference.
  const setLastNotesView = useNotesViewStore((state) => state.setLastNotesView)
  useEffect(() => {
    setLastNotesView(notesView)
  }, [notesView, setLastNotesView])
  return (
    <ParticipantVisitNotesPageComponent
      patientId={patientId}
      view={notesView}
    />
  )
}

function ParticipantVisitNotesPageComponent({
  patientId,
  view,
}: {
  patientId: string
  view: ParticipantNotesView
}): JSX.Element {
  const navigateFromClick = useNavigateFromClickEvent()
  const navigate = useNavigate()
  const location = useLocation()
  const isReadOnly = useIsReadOnly()
  const isAssessmentsView = view === 'assessments'
  const keepTableSort = useFeatureFlag(featureFlags.searchKeepsTableSort)
  const [filters, setFilters] = useState<Filter[]>([])
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('search') ?? ''

  /* React Router's setSearchParams rebuilds from the render's closure, so two
   * writes in one event (a sort, then a page or query reset) would clobber each
   * other. Threading every write through this ref makes the second build on the
   * first. */
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  const updateSearchParams = useCallback(
    (
      mutate: (params: URLSearchParams) => void,
      options?: { replace?: boolean }
    ): void => {
      const next = new URLSearchParams(searchParamsRef.current)
      mutate(next)
      searchParamsRef.current = next
      setSearchParams(next, options)
    },
    [setSearchParams]
  )
  /* Local input state decoupled from the URL so typing never blocks on
   * router updates or MiniSearch index rebuilds. The URL + downstream
   * search hook are driven off the debounced value. */
  const [inputValue, setInputValue] = useState(search)
  const debouncedSearch = useDebounced(inputValue, 1000)

  /* Push the debounced input value to the URL. Skips when already in sync
   * to avoid unnecessary history writes. */
  useEffect(() => {
    const current = searchParamsRef.current.get('search') ?? ''
    if (current === debouncedSearch) {
      return
    }
    updateSearchParams(
      (params) => {
        if (debouncedSearch) {
          params.set('search', debouncedSearch)
        } else {
          params.delete('search')
        }
        /* Flag off, a new query re-ranks from relevance, and a cleared query
         * unmounts the chip, so leftover sort params would be invisible and
         * inherited by the next search. Flag on, the table's sort is the
         * point — it survives both. */
        if (!keepTableSort) {
          params.delete(SORT_BY_PARAM)
          params.delete(SORT_DIR_PARAM)
        }
      },
      { replace: true }
    )
  }, [debouncedSearch, keepTableSort, updateSearchParams])

  /* Sync local input state from the URL on mount/back-nav when the URL
   * diverges from the input (e.g. deep link, browser history). */
  useEffect(() => {
    setInputValue((prev) => (prev === search ? prev : search))
  }, [search])

  const currentPractitioner = useCurrentPractitioner()
  /* "My notes" scopes every note query server-side to notes the current
   * practitioner authored (the FHIR `author` param), on top of the patient
   * scope each query already carries. Spreading `undefined` for "all" is a
   * no-op, so the base queries are unchanged in that view. */
  const authorScope =
    view === 'mine'
      ? { author: currentPractitioner.reference.reference }
      : undefined

  const participantReference = useReference<Patient>(`Patient/${patientId}`)
  /* Interim: fetch up to the 1000-per-page cap (Medplum default) for
   * in-memory client-side search. Long-tail participants with >1000 notes
   * will be handled when Elastic-backed search lands. */
  /* `keepPreviousData` keeps the prior view's rows on screen while the new
   * (All ↔ My) query resolves, so the toggle never drops to the empty-state
   * Loader gate below — no white flash. Works because the All/My toggle keeps
   * this component mounted (single `notes/:view` route). */
  const { data: elationVisitNotes } = useSearch(
    VisitNote,
    {
      ...VisitNote.elationVisitNotesQuery({
        patient: participantReference,
      }),
      ...authorScope,
    },
    { query: { placeholderData: keepPreviousData } }
  )

  const { data: participantNotes } = useSearch(
    ParticipantNote,
    {
      ...ParticipantNote.queryByParticipant(participantReference),
      ...authorScope,
    },
    { query: { placeholderData: keepPreviousData } }
  )

  /* List-layer consolidation: legacy assessments (`category=assessment`) are
   * listed alongside notes. Same client-side-search ceiling as notes — bump
   * `_count` to the 1000 page cap (the query default would otherwise cap at
   * 20) without touching the shared `Assessment.searchQuery`. */
  const { data: assessments } = useSearch(
    Assessment,
    {
      ...Assessment.searchQuery({ subject: participantReference }),
      _count: '1000',
      ...authorScope,
    },
    { query: { placeholderData: keepPreviousData } }
  )

  /* User note labels plus the fixed visit-note categories, offered together as
   * one "Labels" filter facet. */
  const { data: noteLabels } = useSearch(NoteLabel, NoteLabel.baseSearchQuery())

  /* User note labels reuse the shared label-filter option builder (same colored
   * chips as Tasks); the fixed categories are appended as pick-only options. */
  const noteLabelOptions = useLabelFilterOptions(noteLabels)
  const labelOptions = useMemo(
    () => [
      ...noteLabelOptions,
      ...VISIT_NOTE_CATEGORIES.map((category) => ({
        value: visitNoteCategoryToken(category),
        label: <LabelOption color={category.color} label={category.display} />,
        icon: <LabelColor color={category.color} />,
        keywords: [category.display],
      })),
    ],
    [noteLabelOptions]
  )

  /* Optimistic + server-side label filtering. The selected label/category
   * tokens drive an authoritative server token search (labels via `_tag`,
   * categories via `category`) on both label-bearing queries — ParticipantNotes
   * and Assessments; until it resolves we keep showing the already-cached
   * unfiltered set, which `useFilteredData` narrows client-side for an instant,
   * flash-free preview. Only inclusion operators map to a server OR-narrow (a
   * correct superset of any inclusion result), so exclusion operators keep the
   * full set and resolve entirely client-side. */
  const labelFilter = filters.find((f) => f.field === 'labels')
  const labelFilterTokens =
    labelFilter &&
    (labelFilter.operator === 'in' || labelFilter.operator === 'contains') &&
    !labelFilter.value.includes(FilterValue.None)
      ? labelFilter.value
      : []
  /* The Assessments tab pins the server-side search to the assessment-kind
   * category tokens (its defining scope). When the Labels facet is also filtering,
   * its tokens are AND-ed into the same `_filter` so a label match beyond the first
   * server page still loads — parity with the All/My server label narrow. Every
   * other view follows the Labels facet alone. `useFilteredData` still applies the
   * exact client-side narrow on top for inclusion/exclusion semantics. */
  const serverCategoryQuery = isAssessmentsView
    ? combineFilters(
        noteLabelCategoryFilter(ASSESSMENT_CATEGORY_TOKENS),
        noteLabelCategoryFilter(labelFilterTokens)
      )
    : noteLabelCategoryFilter(labelFilterTokens)
  const serverCategoryFilterActive = '_filter' in serverCategoryQuery

  const { data: serverFilteredParticipantNotes } = useSearch(
    ParticipantNote,
    serverCategoryFilterActive
      ? {
          ...ParticipantNote.queryByParticipant(participantReference),
          ...serverCategoryQuery,
          ...authorScope,
        }
      : undefined
  )

  const { data: serverFilteredAssessments } = useSearch(
    Assessment,
    serverCategoryFilterActive
      ? {
          ...Assessment.searchQuery({ subject: participantReference }),
          _count: '1000',
          ...serverCategoryQuery,
          ...authorScope,
        }
      : undefined
  )

  /* Prefer each server-filtered set once it lands — it's the complete match set,
   * including rows beyond the unfiltered query's 1000-row cap. Until then, the
   * cached unfiltered set stands in and is narrowed client-side. */
  const participantNotesForTable = serverCategoryFilterActive
    ? (serverFilteredParticipantNotes ?? participantNotes)
    : participantNotes
  const assessmentsForTable = serverCategoryFilterActive
    ? (serverFilteredAssessments ?? assessments)
    : assessments

  const { isPending: isCreatingNote, mutateAsync: mutateTransaction } =
    useTransactionMutation()

  const mutateElationVisitNote = usePatchMutation<VisitNote>().mutate
  const mutateParticipantNote = usePatchMutation<ParticipantNote>().mutate
  const mutateAssessment = usePatchMutation<Assessment>().mutate

  /* Shared by the same-tab row click and the open-in-new-window button. */
  const createNoteFromTemplate = useCallback(
    async (template: NoteTemplate): Promise<string | undefined> => {
      const result = await mutateTransaction(async (txn) => {
        const encounterRef = txn.create(
          new NonVisitEncounter({
            patient: participantReference,
            practitioner: currentPractitioner.reference,
            startTime: now(),
          })
        )
        const newNote = new ParticipantNote({
          content: template.data,
          author: currentPractitioner.reference,
          participant: participantReference,
          encounter: encounterRef,
          labels: template.labels,
        })
        // Categories aren't a constructor arg; merge the template's onto the
        // fresh note (union is trivial here — the note starts with none).
        newNote.categories = template.categories
        const newNoteReference = txn.create(newNote)
        txn.create(
          new TemplateProvenance({
            composition: newNoteReference,
            template: template.reference,
            author: currentPractitioner.reference,
          })
        )
      })
      const createdNote = result.entry?.find(
        (e) => e.resource?.resourceType === 'Composition'
      )?.resource
      return createdNote ? resolveId(createdNote) : undefined
    },
    [mutateTransaction, participantReference, currentPractitioner]
  )

  /* The Assessments view narrows the merged set to rows carrying an
   * assessment-kind category. This is the client-side source of truth for the
   * tab (server scoping above is the completeness optimization): it drops Elation
   * notes and covers the optimistic window before the server-filtered set lands. */
  const tableData = useMemo<NoteRow[]>(() => {
    const merged = [
      ...(elationVisitNotes ?? []),
      ...(participantNotesForTable ?? []),
      ...(assessmentsForTable ?? []),
    ]
    return isAssessmentsView ? merged.filter(hasAssessmentCategory) : merged
  }, [
    elationVisitNotes,
    participantNotesForTable,
    assessmentsForTable,
    isAssessmentsView,
  ])

  /* Owner/Role filter options derive from the FULL view-scoped set, not the
   * (possibly label-narrowed) `tableData` — otherwise selecting a label would
   * silently shrink the owner/role choices to those present in the filtered
   * subset, which a filter-options list must never do. The Assessments view is a
   * scope (like My notes), so options reflect its assessment-only set. */
  const allNotes = useMemo<NoteRow[]>(() => {
    const merged = [
      ...(elationVisitNotes ?? []),
      ...(participantNotes ?? []),
      ...(assessments ?? []),
    ]
    return isAssessmentsView ? merged.filter(hasAssessmentCategory) : merged
  }, [elationVisitNotes, participantNotes, assessments, isAssessmentsView])

  /* Staff directory, including deactivated accounts. Assignment pickers stay
   * active-only; this list is only for matching notes already on the chart. */
  const internalPractitioners = usePractitionersInternal([], {
    includeInactive: true,
  })
  const practitionersByReference = useMemo(() => {
    const map = new Map<string, ExtendedPractitioner>()
    for (const practitioner of internalPractitioners) {
      const ref = practitioner.reference.reference
      if (ref) {
        map.set(ref, practitioner)
      }
    }
    return map
  }, [internalPractitioners])

  /* A note author who is not in the staff directory (no role identifier, or
   * the reference was stored with no display name) is still a Practitioner.
   * Read those records directly so the Owner filter can show a name. */
  const unresolvedOwnerIds = useMemo(() => {
    const ids: string[] = []
    for (const note of allNotes) {
      const owner = getNoteOwnerReferenceObject(note)
      if (!owner?.reference || practitionersByReference.has(owner.reference)) {
        continue
      }
      const id = resolveId(owner)
      if (id) {
        ids.push(id)
      }
    }
    return uniq(ids).sort()
  }, [allNotes, practitionersByReference])
  const unresolvedOwnerQuery = useMemo(
    () =>
      unresolvedOwnerIds.length > 0
        ? {
            _id: unresolvedOwnerIds.join(','),
            _count: String(unresolvedOwnerIds.length),
          }
        : undefined,
    [unresolvedOwnerIds]
  )
  const { data: unresolvedOwners } = useSearch(
    ExtendedPractitioner,
    unresolvedOwnerQuery
  )
  const ownersByReference = useMemo(() => {
    const map = new Map(practitionersByReference)
    for (const practitioner of unresolvedOwners ?? []) {
      const ref = practitioner.reference.reference
      if (ref) {
        map.set(ref, practitioner)
      }
    }
    return map
  }, [practitionersByReference, unresolvedOwners])

  const ownerOptions = useMemo(
    () =>
      uniqBy(
        allNotes.flatMap((note) => {
          const owner = getNoteOwnerReferenceObject(note)
          if (!owner?.reference) {
            return []
          }
          const practitioner = ownersByReference.get(owner.reference)
          const label = ownerFilterLabel(owner, practitioner)

          return {
            value: owner.reference,
            label,
            keywords: [label],
          }
        }),
        'value'
      ),
    [allNotes, ownersByReference]
  )

  /* Resolve each note owner's discipline(s) so notes can be filtered by
   * role (e.g. all OT notes), not just by individual practitioner. Roles
   * live on the Practitioner. Deactivated staff stay in this map: their
   * notes are still on the chart, and Owner Role is a role, not a picker
   * of people who can be assigned new work. */
  const ownerRolesByReference = useMemo(() => {
    const map = new Map<string, Role[]>()
    for (const practitioner of ownersByReference.values()) {
      const ref = practitioner.reference.reference
      const roles = practitioner.idtRoles
      if (ref && roles && roles.length > 0) {
        map.set(ref, roles)
      }
    }
    return map
  }, [ownersByReference])

  /* Only offer roles actually present among this participant's note owners,
   * preserving the canonical IDT role order/labels. */
  const ownerRoleOptions = useMemo(() => {
    const present = new Set<string>()
    for (const note of allNotes) {
      const ref = getNoteOwnerReference(note)
      for (const role of ownerRolesByReference.get(ref) ?? []) {
        present.add(role)
      }
    }
    return IDT_ROLE_OPTIONS_WITH_KEYWORDS.filter((o) =>
      present.has(o.value)
    ).map((o) => ({
      value: o.value,
      label: o.label,
      keywords: o.keywords.filter((k): k is string => !!k),
    }))
  }, [allNotes, ownerRolesByReference])

  /* No Type filter: notes and assessments are intentionally unified, and the
   * row's type label is shown inline (assessment subtitle). A type-narrowing
   * chip would be redundant + misleading since many notes are conceptually
   * "assessments" without being `category=assessment`. */
  const filterOptions = useMemo<FilterConfigMap<NoteRow>>(
    () => ({
      /* One "Labels" facet covering both user note labels and the fixed
       * visit-note categories, mirroring the Tasks page label filter (colored
       * chips, multi-select-array). Selection also drives the server-side token
       * search above; this client-side facet is the instant optimistic layer and
       * the source of truth for exclusion operators. */
      labels: {
        label: 'Labels',
        icon: <IconLabel />,
        type: 'multi-select-array' as const,
        options: labelOptions,
        valueFn: getNoteLabelTokens,
      },
      owner: {
        label: 'Owner',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: ownerOptions,
        valueFn: getNoteOwnerReference,
      },
      ownerRole: {
        label: 'Owner Role',
        icon: <IconUsersGroup className="size-4" />,
        type: 'multi-select-array',
        options: ownerRoleOptions,
        valueFn: (note) =>
          ownerRolesByReference.get(getNoteOwnerReference(note)) ?? [],
      },
      date: {
        label: 'Date',
        icon: <IconCalendarEvent className="size-4" />,
        type: 'date',
        valueFn: (note) => {
          if (note instanceof VisitNote) {
            return note.date ?? ''
          }
          if (note instanceof Assessment) {
            return note.startsAt ?? ''
          }
          return note.period ?? ''
        },
      },
      signedDate: {
        label: 'Signed Date',
        icon: <IconCalendarEvent className="size-4" />,
        type: 'date',
        valueFn: (note) => {
          if (note instanceof VisitNote) {
            return note.signedDate ?? ''
          }

          return note.signedOn ?? ''
        },
      },
    }),
    [ownerOptions, ownerRoleOptions, ownerRolesByReference, labelOptions]
  )

  const filteredTableData = useFilteredData(tableData, filterOptions, filters)

  /* MiniSearch-backed full-text search (inverted index + BM25). Field
   * boosts reflect user-visible prominence: title and summary outrank
   * reason / body content. Empty query returns `filteredTableData`
   * unchanged so column sorts are preserved; non-empty queries are
   * scored-order. */
  const { search: searchNotes, getExtractedFields } = useClientSideSearch(
    filteredTableData,
    {
      fields: ['title', 'summary', 'body', 'author'],
      boost: { title: 5, summary: 3, author: 2, body: 1 },
      extractFields: extractNoteSearchFields,
      /* Defer index build to idle so large-N-notes patients don't block the
       * main thread on mount / on data identity changes. */
      idleBuild: true,
    }
  )

  const { items: searchFilteredTableData, matchById } = useMemo(
    () => searchNotes(debouncedSearch),
    [searchNotes, debouncedSearch]
  )

  const isSearching = debouncedSearch.trim().length > 0

  const columns = useMemo<ColumnDef<NoteRow>[]>(
    () => [
      {
        id: 'name',
        header: 'Note',
        enableSorting: true,
        accessorFn: getNoteName,
        sortingFn: sortNoteText,
        cell: ({ row }) => (
          <VisitNoteTitleCell
            note={row.original}
            match={row.original.id ? matchById.get(row.original.id) : undefined}
            extractedFields={
              row.original.id ? getExtractedFields(row.original.id) : undefined
            }
          />
        ),
      },
      {
        id: 'date',
        header: 'Created',
        size: COLUMN_WIDTHS.date,
        enableSorting: true,
        sortDescFirst: true,
        accessorFn: getNoteCreated,
        cell: ({ row }) => {
          const note = row.original
          const date =
            note instanceof VisitNote
              ? note.date
              : note instanceof Assessment
                ? note.startsAt
                : note.period
          return date ? <HoverDate date={date} /> : '---'
        },
      },
      {
        id: 'signedDate',
        header: 'Signed',
        size: COLUMN_WIDTHS.signed,
        enableSorting: true,
        sortDescFirst: true,
        accessorFn: getNoteSigned,
        cell: ({ row }) => {
          const note = row.original
          if (note instanceof VisitNote) {
            return note.signedDate ? (
              <HoverDate date={note.signedDate} />
            ) : (
              '---'
            )
          }
          return note.signedOn ? <HoverDate date={note.signedOn} /> : '---'
        },
      },
      /* Owner avatar lives far-right, sized to a 20px icon avatar to match the
       * Tasks list. Its header is blank by design, so the sort chip takes its
       * label from `meta.sortLabel` instead. */
      {
        id: 'owner',
        header: '',
        meta: { sortLabel: 'Owner' },
        size: COLUMN_WIDTHS.owner,
        enableSorting: true,
        accessorFn: getNoteOwner,
        sortingFn: sortNoteText,
        cell: ({ row }) => {
          const note = row.original
          const signer = getRowSigner(note)
          if (signer) {
            return <HoverPractitionerAvatar practitioner={signer} size="icon" />
          }
          const owner = getRowAuthor(note)
          return isOfType(owner, 'Practitioner') ? (
            <HoverPractitionerAvatar practitioner={owner} size="icon" />
          ) : null
        },
      },
    ],
    [matchById, getExtractedFields]
  )

  /* The chip's options and the `?sortBy=` parser read the same derived list, so
   * an offered sort is always one the table can actually render. */
  const sortableColumns = useMemo(() => getSortableColumns(columns), [columns])

  const sorting = useMemo<SortingState>(() => {
    const id = searchParams.get(SORT_BY_PARAM)
    const column = id
      ? sortableColumns.find((candidate) => candidate.id === id)
      : undefined
    /* An unrecognized id — a stale `?sortBy=created` link from the old
     * implementation, say — falls through to the default rather than producing
     * a sort no column can render. */
    if (column) {
      const dir = searchParams.get(SORT_DIR_PARAM)
      return [
        { id: column.id, desc: dir ? dir === 'desc' : column.sortDescFirst },
      ]
    }
    if (keepTableSort) {
      /* Relevance is the search hook's own ordering, so it only means
       * something while searching. */
      return isSearching && id === RELEVANCE_SORT_BY ? [] : DEFAULT_SORTING
    }
    return isSearching ? [] : DEFAULT_SORTING
  }, [searchParams, isSearching, keepTableSort, sortableColumns])

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater
      updateSearchParams(
        (params) => {
          if (next.length === 0) {
            params.delete(SORT_DIR_PARAM)
            /* Flag on, an empty sort while searching has to be written:
             * a missing `sortBy` now means the table default, not relevance. */
            if (isSearching && keepTableSort) {
              params.set(SORT_BY_PARAM, RELEVANCE_SORT_BY)
            } else {
              params.delete(SORT_BY_PARAM)
            }
          } else {
            params.set(SORT_BY_PARAM, next[0].id)
            params.set(SORT_DIR_PARAM, next[0].desc ? 'desc' : 'asc')
          }
        },
        { replace: true }
      )
    },
    [sorting, isSearching, keepTableSort, updateSearchParams]
  )

  if (!elationVisitNotes) {
    return <Loader />
  }

  return (
    // Header/toolbar layout mirrors the participant Documents page
    // (PatientDocumentsPage + DocumentReferencesTable).
    <div className="grid">
      <div className="flex items-center justify-between gap-4">
        <TypographyPageTitle>Notes</TypographyPageTitle>
        {!isReadOnly && (
          <TemplateCombobox
            templateClass={NoteTemplate}
            onSelect={async (template) => {
              const noteId = await createNoteFromTemplate(template)
              if (noteId) {
                void navigate(`/Patient/${patientId}/visit-notes/${noteId}`)
              } else {
                toast.error('Failed to create note')
              }
            }}
            onOpenInNewWindow={(template) => {
              /* Open an empty popup synchronously inside the click gesture so
               * the browser doesn't block it; we point it at the note once
               * the (async) creation resolves. See openInNewWindow for why a
               * chromeless popup is the only way to force a separate window. */
              const popup = openInNewWindow('')
              void createNoteFromTemplate(template)
                .then((noteId) => {
                  if (!noteId) {
                    popup?.close()
                    toast.error('Failed to create note')
                    return
                  }
                  const path = `/Patient/${patientId}/visit-notes/${noteId}`
                  if (popup && !popup.closed) {
                    popup.location.href = `${window.location.origin}${path}`
                  } else {
                    // Popup was blocked or the user closed it before creation
                    // finished. The note exists, so fall back to same-tab nav
                    // rather than leaving the user on the list with nothing.
                    void navigate(path)
                  }
                })
                .catch(() => {
                  popup?.close()
                  toast.error('Failed to create note')
                })
            }}
          >
            <Button
              variant="outline"
              className="flex items-center gap-2"
              loading={isCreatingNote}
            >
              <IconPlus className="size-4" />
              New note…
            </Button>
          </TemplateCombobox>
        )}
      </div>
      <div className="overflow-hidden">
        <div className="flex items-center gap-2 py-2">
          <div className="min-w-0 flex-1">
            {/* A tab narrows the result set rather than re-ranking it, so the
                query string (search + sort) rides along to the new view. */}
            <Tabs value={view}>
              <TabsList>
                <TabsTrigger
                  value="all"
                  onClick={() =>
                    navigate(
                      `/Patient/${patientId}/notes/all${location.search}`
                    )
                  }
                >
                  All notes
                </TabsTrigger>
                <TabsTrigger
                  value="mine"
                  onClick={() =>
                    navigate(
                      `/Patient/${patientId}/notes/mine${location.search}`
                    )
                  }
                >
                  My notes
                </TabsTrigger>
                <TabsTrigger
                  value="assessments"
                  onClick={() =>
                    navigate(
                      `/Patient/${patientId}/notes/assessments${location.search}`
                    )
                  }
                >
                  Assessments
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isSearching && (
              <SearchSortChip
                columns={columns}
                sorting={sorting}
                onSortingChange={handleSortingChange}
              />
            )}
            <ShortcutSearchInput
              value={inputValue}
              onChange={setInputValue}
              className="w-48 shrink-0"
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 py-2">
          <FilterComposer<NoteRow>
            id="participant-visit-notes-table-filters"
            filterOptions={filterOptions}
            onChange={setFilters}
          />
        </div>
        <DataTable
          columns={columns}
          data={searchFilteredTableData}
          enableSorting
          onSortingChange={handleSortingChange}
          state={{ sorting }}
          onRowClick={(row, e) =>
            navigateFromClick(getRowHref(row, patientId), e)
          }
          contextMenuContent={(row) => {
            const href = getRowHref(row, patientId)
            return (
              <>
                <ContextMenuOpenInNewTab href={href} />
                <ContextMenuOpenInNewWindow href={href} />
              </>
            )
          }}
          deleteRowProps={(row) => ({
            title: `Delete "${extractTitleFromHTML(row.title ?? '')}"?`,
            description: `Deleted ${
              row instanceof Assessment ? 'assessments' : 'notes'
            } can still be recovered by contacting our team.`,
            onConfirm: () => {
              const item = tableData.find((v) => v.id === row.id)
              invariant(item, 'Row is required')
              if (item instanceof VisitNote) {
                mutateElationVisitNote({
                  instance: item,
                  mutateFn: (a) => {
                    a.status = 'entered-in-error'
                  },
                })
              } else if (item instanceof Assessment) {
                mutateAssessment({
                  instance: item,
                  mutateFn: (a) => {
                    a.status = 'entered-in-error'
                  },
                })
              } else {
                mutateParticipantNote({
                  instance: item,
                  mutateFn: (a) => {
                    a.status = 'entered-in-error'
                  },
                })
              }
            },
          })}
        />
      </div>
    </div>
  )
}

function VisitNoteTitleCell({
  note,
  match,
  extractedFields,
}: {
  note: NoteRow
  match: MatchInfo | undefined
  extractedFields: Record<string, string> | undefined
}): JSX.Element {
  const isAssessment = note instanceof Assessment
  const isElation = note instanceof VisitNote && note.elationId != null
  const terms = match?.terms
  /* Fallback runs only when the idle index hasn't built yet for this row. */
  const fallbackFields = useMemo(
    () => extractedFields ?? extractNoteSearchFields(note),
    [extractedFields, note]
  )
  /* The row's name comes from the shared derivation, not from the search
     index beside it: the same helper names this note wherever else the product
     shows it, and the index exists to be matched against, not displayed.
     Memoized because naming an Orbit-native note round-trips its content
     through TipTap, which is too much to redo on every render of every row. */
  const title = useMemo(() => getNoteName(note), [note])
  const summary = fallbackFields.summary.trim()
  const stems = useMemo(() => terms ?? [], [terms])
  const { perField } =
    stems.length > 0
      ? countStemOccurrencesMulti(fallbackFields, stems)
      : { perField: {} as Record<string, number> }
  const bodyMatches = perField.body ?? 0

  /* Assessments have no AI summary; their subtitle is the type display label
   * (e.g. "180 Days"), mirroring the legacy Assessments page. Notes fall back
   * to the AI-summary placeholder — `aiSummaryGeneratedAt` set with no summary
   * is the cron's "tried and found nothing" sentinel ("No content"). */
  const assessmentTypeLabel = isAssessment
    ? (displayFromCodeableConcept(note.type) ?? '')
    : ''
  const summaryPlaceholder = isAssessment
    ? ''
    : note.aiSummaryGeneratedAt
      ? 'No content'
      : 'Summary pending…'

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="flex min-w-0 items-center gap-1.5">
        <HighlightedText
          text={title}
          terms={terms}
          className="line-clamp-1 font-semibold text-gray-900 dark:text-foreground"
        />
        {isElation && (
          <Badge variant="outline" color="blue" className="shrink-0">
            Elation
          </Badge>
        )}
      </span>
      {isAssessment ? (
        assessmentTypeLabel ? (
          <HighlightedText
            text={assessmentTypeLabel}
            terms={terms}
            className="text-sm text-gray-600 dark:text-foreground-secondary"
          />
        ) : null
      ) : summary ? (
        <HighlightedText
          text={summary}
          terms={terms}
          className="line-clamp-3 text-sm text-gray-600 dark:text-foreground-secondary"
        />
      ) : (
        <span className="text-sm text-gray-600 italic dark:text-foreground-secondary">
          {summaryPlaceholder}
        </span>
      )}
      {bodyMatches > 0 && (
        <SearchMatchSnippet
          hiddenFieldText={fallbackFields.body}
          stemmedTerms={stems}
          hiddenFieldMatches={bodyMatches}
        />
      )}
    </div>
  )
}
